import { useQuery } from "@tanstack/react-query"
import { Plus } from "lucide-react"
import { useMemo, useState } from "react"

import { api } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError, EstadoVacio } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

import { useCatalogo } from "./Catalogo"
import { cifra } from "./descarga"
import { EditorRecibo } from "./EditorRecibo"
import { type Empresa, ESTADOS, MONEDAS, numero, type Recibo, TONO_ESTADO } from "./tipos"

/** El historial de los fletes que coordinó el intermediario, con sus totales por moneda. */
export function Recibos() {
  const recibos = useQuery({
    queryKey: ["transporte", "recibos"],
    queryFn: () => api.get<Recibo[]>("/api/v1/transporte/recibos"),
  })
  const empresas = useCatalogo<Empresa>("EMPRESA")
  const [abierto, setAbierto] = useState<Recibo | "nuevo" | null>(null)
  const [estado, setEstado] = useState("")

  const lista = useMemo(
    () => (recibos.data ?? []).filter((r) => !estado || r.estado === estado),
    [recibos.data, estado],
  )
  const totales = useMemo(() => {
    const t: Record<string, number> = {}
    for (const r of recibos.data ?? []) {
      if ((r.estado === "EMITIDO" || r.estado === "RECIBIDO") && r.total) {
        t[r.moneda] = (t[r.moneda] ?? 0) + Math.round(Number(r.total) * 100)
      }
    }
    return t
  }, [recibos.data])
  const nombre = (id: number | null) => empresas.data?.find((e) => e.id === id)?.razon_social ?? "—"

  if (recibos.isPending) return <EstadoCarga etiqueta="Cargando recibos" />
  if (recibos.isError) {
    return (
      <EstadoError
        titulo="No se pudieron cargar los recibos"
        error={recibos.error}
        onReintentar={() => recibos.refetch()}
      />
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <label className="grid gap-1 text-sm">
          Estado
          <select className="campo" value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(ESTADOS).map(([v, t]) => (
              <option key={v} value={v}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <p className="text-sm tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {Object.entries(totales).map(([m, c]) => (
            <span key={m} className="mr-3">
              Emitido: {cifra(c / 100)} {MONEDAS[m as keyof typeof MONEDAS]}
            </span>
          ))}
        </p>
        <Boton variante="primario" onClick={() => setAbierto("nuevo")}>
          <Plus size={16} aria-hidden={true} /> Nuevo recibo
        </Boton>
      </div>
      {lista.length === 0 ? (
        <EstadoVacio
          titulo="Sin recibos"
          texto="Cada flete que coordine queda aquí, con su número y su PDF."
        />
      ) : (
        <Tabla>
          <thead>
            <tr>
              <Th>Nº</Th>
              <Th>Fecha</Th>
              <Th>Empresa</Th>
              <Th>BL / destino</Th>
              <Th className="text-right">Total</Th>
              <Th>Estado</Th>
            </tr>
          </thead>
          <tbody>
            {lista.map((r) => (
              <tr key={r.id} className="cursor-pointer" onClick={() => setAbierto(r)}>
                <Td className="tabular-nums">
                  <button
                    type="button"
                    className="underline-offset-2 hover:underline"
                    onClick={() => setAbierto(r)}
                  >
                    {r.numero ? numero(r.numero) : "Borrador"}
                  </button>
                </Td>
                <Td className="tabular-nums">{comoFecha(r.fecha)}</Td>
                <Td>{nombre(r.empresa_id)}</Td>
                <Td>{[r.bl, r.destino].filter(Boolean).join(" · ") || "—"}</Td>
                <Td className="text-right tabular-nums">
                  {r.total ? `${cifra(r.total)} ${MONEDAS[r.moneda]}` : "—"}
                </Td>
                <Td>
                  <Insignia tono={TONO_ESTADO[r.estado]}>{ESTADOS[r.estado]}</Insignia>
                </Td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}
      {abierto && (
        <EditorRecibo
          recibo={abierto === "nuevo" ? null : abierto}
          onCerrar={() => setAbierto(null)}
        />
      )}
    </div>
  )
}
